import { createContext } from "react";
import { useContext, useEffect, useState } from "react";
import { getAllWorkStreamsByProjectId } from "../api/Api";
import useFetch from "../hooks/useFetch";


const ProjectContext = createContext(null);

/**
 * ProjectContext provides a centralized state management for project-related data
 * across the application. It handles fetching, caching, and refreshing of project data.
 * 
 * @module ProjectContext
 */

/**
 * Custom hook to access the ProjectContext
 * @returns {Object} The project context containing:
 *   - data: Array of project data
 *   - error: Any error that occurred during data fetching
 *   - loading: Boolean indicating if data is being fetched
 *   - refresh: Function to manually refresh the project data
 * @throws {Error} If used outside of ProjectContextProvider
 * 
 * @example
 * const { data, loading, error, refresh } = useProject();
 */

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error("useProject must be used within a ProjectContextProvider");
  }
  return context;
};

/**
 * ProjectContextProvider component that manages project data state and provides it to child components
 * 
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components that will have access to the project context
 * 
 * @example
 * <ProjectContextProvider>
 *   <YourComponent />
 * </ProjectContextProvider>
 */

function ProjectContextProvider({ children }) {
  const [workStreams, setWorkStreams] = useState({data: [], loading: false, error: null});

  const { data, error, loading, refresh } = useFetch(
    `${process.env.REACT_APP_BACKEND_BASE_URL}/projects/`
  );

  const getWorkStreams = async (projectId) => {
    if (!projectId) {
      console.error("Project ID is required to fetch work streams.");
      return;
    }
    setWorkStreams({data: [], loading: true, error: null});
    try {
      setWorkStreams({data: [], loading: true, error: null});
      const response = await getAllWorkStreamsByProjectId(projectId);
      if (response?.status === 200) {
        setWorkStreams(prev => ({...prev, data: response.data.initiatives, loading: false, error: null}));
      } else {
        setWorkStreams(prev => ({...prev, loading: false, error: response.error}));
      }
    } catch (error) {
      setWorkStreams(prev => ({...prev, loading: false, error: error}));
    } finally {
      setWorkStreams(prev => ({...prev, loading: false, error: null}));
    }
  }

  useEffect(() => {
    getWorkStreams();
  }, []);

  const value = {
    data,      // Array of project data
    error,     // Any error that occurred during fetching
    loading,   // Loading state indicator
    refresh,  // Function to manually refresh the data
    workStreams, // Array of work streams
    getWorkStreams, // Function to get work streams
  };

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
}

export { ProjectContext };
export default ProjectContextProvider;


// ------------------------------------------------------------------------------------------------
// example of data
// "data": [
//       {
//           "id": 43,
//           "content": "changed project status from 'underReview' to 'active'",
//           "content_type": "Project",
//           "content_id": 30,
//           "timestamp": "2025-05-21T05:30:22.493Z",
//           "user": {
//               "id": 11,
//               "first_name": "Johnss",
//               "last_name": "Doess"
//           }
//       },
//       {
//           "id": 42,
//           "content": "changed project status from 'planning' to 'underReview'",
//           "content_type": "Project",
//           "content_id": 30,
//           "timestamp": "2025-05-21T05:30:19.718Z",
//           "user": {
//               "id": 11,
//               "first_name": "Johnss",
//               "last_name": "Doess"
//           }
//       }
//   ]